import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  startOfDay,
  addDays,
  addMonths,
  subMonths,
  isSameDay,
  isSameMonth,
  differenceInMinutes,
} from 'date-fns'
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  PhoneIncoming,
  PhoneOutgoing,
  MessageSquare,
  CheckSquare,
  ExternalLink,
  X,
  Star,
  CheckCircle2,
  AlertCircle,
  StickyNote,
  FileText,
  ChevronDown,
} from 'lucide-react'
import api from '@/lib/api'
import { getUserTimezone } from '@/lib/timezone'
import { cn, formatPhoneDisplay } from '@/lib/utils'
import { onSocketEvent } from '@/hooks/useWebSocket'
import {
  getBookingCalendarDate,
  type CalendarBooking,
} from './BookingsCalendar'

export const PEEK_RAIL_WIDTH_PX = 320

interface ConnectedCalendarEvent {
  id: string
  summary: string
  start: string
  end: string
  allDay: boolean
  htmlLink?: string
  location?: string
  description?: string
}

type PeekEventSource = 'inbound' | 'outbound' | 'chat' | 'task' | 'callback' | 'calendar'

interface PeekEvent {
  id: string
  source: PeekEventSource
  title: string
  start: Date
  end: Date | null
  allDay: boolean
  // Reference back to original record for the detail drawer
  booking?: CalendarBooking
  calendar?: ConnectedCalendarEvent
}

interface CalendarPeekRailProps {
  open: boolean
  onToggle: () => void
  topOffsetClass: string
}

// Color stripe by source — matches BookingsPage.PILL_TONE conventions so the
// rail and the full calendar feel like the same product.
const SOURCE_STYLES: Record<PeekEventSource, { stripe: string; label: string; icon: React.ElementType }> = {
  inbound:  { stripe: 'bg-brand-royal',       label: 'Inbound call',  icon: PhoneIncoming },
  outbound: { stripe: 'bg-brand-periwinkle',  label: 'Outbound call', icon: PhoneOutgoing },
  chat:     { stripe: 'bg-brand-indigo',      label: 'Chat booking',  icon: MessageSquare },
  task:     { stripe: 'bg-amber-500',         label: 'Task',          icon: CheckSquare },
  callback: { stripe: 'bg-emerald-500',       label: 'Callback',      icon: PhoneOutgoing },
  calendar: { stripe: 'bg-violet-500', label: 'Connected calendar', icon: CalendarIcon },
}

function bookingToPeekEvent(b: CalendarBooking): PeekEvent | null {
  // Use the same resolver the calendar grid uses so the rail and the full
  // BookingsCalendar agree on wall-clock time. parseISO(b.date) alone yields
  // created_at — fine for instant log entries, wrong for AI-scheduled
  // appointments where the real time lives in scheduledDate/scheduledTime.
  const start = getBookingCalendarDate(b)
  if (Number.isNaN(start.getTime())) return null
  let source: PeekEventSource = 'chat'
  if (b.type === 'task') source = 'task'
  else if (b.bookingType === 'callback_followup') source = 'callback'
  else if (b.source === 'Inbound Call') source = 'inbound'
  else if (b.source === 'Outbound Call') source = 'outbound'
  else if (b.type === 'chat') source = 'chat'

  const title =
    b.callerName ||
    (b.phone ? formatPhoneDisplay(b.phone) : null) ||
    b.summary ||
    (b.type === 'task' ? 'Task' : 'Booking')
  // Duration is the call/chat duration, not the appointment length — only
  // useful for live-event rendering. Skip it for upcoming appointments;
  // those should display as a single-time pill.
  return {
    id: `booking-${b.id}`,
    source,
    title,
    start,
    end: null,
    allDay: source === 'callback',
    booking: b,
  }
}

function calendarToPeekEvent(g: ConnectedCalendarEvent): PeekEvent | null {
  // All-day events come back as YYYY-MM-DD dates; non-all-day come as ISO datetimes.
  const start = g.allDay ? new Date(`${g.start}T00:00:00`) : new Date(g.start)
  const end = g.allDay ? new Date(`${g.end}T00:00:00`) : new Date(g.end)
  if (Number.isNaN(start.getTime())) return null
  return {
    id: `calendar-${g.id}`,
    source: 'calendar',
    title: g.summary,
    start,
    end,
    allDay: g.allDay,
    calendar: g,
  }
}

function formatEventTime(ev: PeekEvent): string {
  if (ev.allDay) return 'All day'
  const startStr = format(ev.start, 'h:mm a')
  if (!ev.end) return startStr
  const minutes = differenceInMinutes(ev.end, ev.start)
  if (minutes <= 0) return startStr
  if (minutes >= 60) {
    const hrs = Math.floor(minutes / 60)
    const rem = minutes % 60
    return rem === 0 ? `${startStr} · ${hrs}h` : `${startStr} · ${hrs}h ${rem}m`
  }
  return `${startStr} · ${minutes}m`
}

// Group events by day, ordered chronologically.
function groupByDay(events: PeekEvent[], from: Date, dayCount: number): Array<{ day: Date; events: PeekEvent[] }> {
  const buckets: Array<{ day: Date; events: PeekEvent[] }> = []
  for (let i = 0; i < dayCount; i++) {
    const day = startOfDay(addDays(from, i))
    buckets.push({ day, events: [] })
  }
  for (const ev of events) {
    for (const bucket of buckets) {
      if (isSameDay(ev.start, bucket.day)) {
        bucket.events.push(ev)
        break
      }
    }
  }
  for (const bucket of buckets) {
    bucket.events.sort((a, b) => {
      if (a.allDay && !b.allDay) return -1
      if (!a.allDay && b.allDay) return 1
      return a.start.getTime() - b.start.getTime()
    })
  }
  return buckets
}

function dayLabel(day: Date, today: Date): string {
  if (isSameDay(day, today)) return 'Today'
  if (isSameDay(day, addDays(today, 1))) return 'Tomorrow'
  return format(day, 'EEEE')
}

export default function CalendarPeekRail({ open, onToggle, topOffsetClass }: CalendarPeekRailProps) {
  const navigate = useNavigate()
  const today = React.useMemo(() => startOfDay(new Date()), [])
  const [cursorMonth, setCursorMonth] = React.useState<Date>(today)
  const [selectedDay, setSelectedDay] = React.useState<Date>(today)
  const [bookings, setBookings] = React.useState<CalendarBooking[]>([])
  const [calendarEvents, setCalendarEvents] = React.useState<ConnectedCalendarEvent[]>([])
  const [calendarConnected, setCalendarConnected] = React.useState<boolean>(false)
  const [loading, setLoading] = React.useState<boolean>(false)
  const [detail, setDetail] = React.useState<PeekEvent | null>(null)

  // Fetch when the rail opens. Skip while closed to avoid wasted API calls.
  // Also re-runs whenever `reloadKey` ticks (socket-driven refresh below).
  const [reloadKey, setReloadKey] = React.useState(0)
  React.useEffect(() => {
    if (!open) return
    let cancelled = false
    const load = async () => {
      setLoading(true)
      // Window: cursor month start (or 1 week back, whichever is earlier) → +60 days.
      // Wide enough to cover mini-month indicators + agenda.
      const monthStart = startOfMonth(cursorMonth)
      const windowStart = monthStart < addDays(today, -7) ? monthStart : addDays(today, -7)
      const windowEnd = addDays(endOfMonth(cursorMonth), 60)
      const tz = encodeURIComponent(getUserTimezone())
      try {
        const [bookingsRes, calendarRes] = await Promise.all([
          api.get(`/api/analytics/bookings?timeframe=all&timezone=${tz}`).catch(() => null),
          api
            .get(`/api/integrations/calendar/events`, {
              params: { start: windowStart.toISOString(), end: windowEnd.toISOString() },
            })
            .catch(() => null),
        ])
        if (cancelled) return
        if (bookingsRes?.data?.success) {
          setBookings(bookingsRes.data.bookings || [])
        }
        if (calendarRes?.data) {
          setCalendarConnected(Boolean(calendarRes.data.connected))
          setCalendarEvents(calendarRes.data.events || [])
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [open, cursorMonth, today, reloadKey])

  // Live refresh: server emits `data_changed` whenever any booking surface
  // mutates (new chat booking, inbound call mark, manual task add, delete, etc).
  // Bump reloadKey so the fetch effect re-runs without leaving the rail stale.
  React.useEffect(() => {
    if (!open) return
    const cleanups = [
      onSocketEvent('data_changed', (payload: any) => {
        if (payload?.type === 'bookings') setReloadKey(k => k + 1)
      }),
      // Chat-booking-completed event fires immediately when a visitor confirms
      // a slot — server emits this in addition to data_changed. Listening to
      // both keeps the rail in sync even if one path drops a packet.
      onSocketEvent('booking:completed', () => setReloadKey(k => k + 1)),
    ]
    return () => cleanups.forEach(fn => fn())
  }, [open])

  const allEvents = React.useMemo<PeekEvent[]>(() => {
    const out: PeekEvent[] = []
    for (const b of bookings) {
      const ev = bookingToPeekEvent(b)
      if (ev) out.push(ev)
    }
    for (const g of calendarEvents) {
      const ev = calendarToPeekEvent(g)
      if (ev) out.push(ev)
    }
    return out
  }, [bookings, calendarEvents])

  // Indicator dots for the mini-month — set of YYYY-MM-DD strings that have any event.
  const eventDays = React.useMemo(() => {
    const set = new Set<string>()
    for (const ev of allEvents) {
      set.add(format(ev.start, 'yyyy-MM-dd'))
    }
    return set
  }, [allEvents])

  // Agenda window: selectedDay → +7 days.
  const agendaBuckets = React.useMemo(
    () => groupByDay(allEvents, selectedDay, 7),
    [allEvents, selectedDay]
  )

  const handleSelectDay = (day: Date) => {
    setSelectedDay(startOfDay(day))
    if (!isSameMonth(day, cursorMonth)) setCursorMonth(day)
  }

  const handleEventClick = (ev: PeekEvent) => {
    setDetail(ev)
  }

  const handleOpenInCalendar = (ev: PeekEvent) => {
    if (ev.calendar?.htmlLink) {
      window.open(ev.calendar.htmlLink, '_blank', 'noopener,noreferrer')
      return
    }
    if (ev.booking) {
      navigate(`/bookings?bookingId=${ev.booking.id}`)
    }
  }

  return (
    <>
      {/* Toggle button — mirrors the sidebar's pull-tab pattern but on the right edge.
          Always rendered so users can pop the rail back open after closing. */}
      <button
        type="button"
        onClick={onToggle}
        aria-label={open ? 'Hide calendar peek' : 'Show calendar peek'}
        title={open ? 'Hide calendar' : 'Show calendar'}
        className={cn(
          'fixed top-20 z-50 h-6 w-6 rounded-full border border-border bg-background shadow-md hover:bg-accent flex items-center justify-center transition-[right] duration-200',
        )}
        style={{ right: open ? `${PEEK_RAIL_WIDTH_PX - 12}px` : '12px' }}
      >
        {open ? (
          <ChevronRight className="h-4 w-4" />
        ) : (
          <ChevronLeft className="h-4 w-4" />
        )}
      </button>

      <aside
        aria-hidden={!open}
        className={cn(
          'fixed right-0 z-40 border-l border-border/50 bg-card/95 backdrop-blur-xl transition-transform duration-200 flex flex-col',
          topOffsetClass,
          open ? 'translate-x-0' : 'translate-x-full pointer-events-none'
        )}
        style={{ width: `${PEEK_RAIL_WIDTH_PX}px` }}
      >
        {/* Header: month nav */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border/40">
          <h3 className="text-sm font-heading font-semibold tracking-tight">
            {format(cursorMonth, 'MMMM yyyy')}
          </h3>
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              onClick={() => setCursorMonth(prev => subMonths(prev, 1))}
              className="h-7 w-7 rounded-md hover:bg-accent flex items-center justify-center text-muted-foreground"
              aria-label="Previous month"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => {
                setCursorMonth(today)
                setSelectedDay(today)
              }}
              className="h-7 px-2 rounded-md hover:bg-accent text-[11px] font-medium text-muted-foreground"
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => setCursorMonth(prev => addMonths(prev, 1))}
              className="h-7 w-7 rounded-md hover:bg-accent flex items-center justify-center text-muted-foreground"
              aria-label="Next month"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Mini-month grid */}
        <div className="px-3 pt-2 pb-3 border-b border-border/40">
          <MiniMonth
            cursorMonth={cursorMonth}
            selectedDay={selectedDay}
            today={today}
            eventDays={eventDays}
            onSelect={handleSelectDay}
          />
        </div>

        {/* Agenda */}
        <div className="flex-1 overflow-y-auto overscroll-contain">
          {loading && agendaBuckets.every(b => b.events.length === 0) ? (
            <div className="px-4 py-8 text-center text-xs text-muted-foreground">Loading…</div>
          ) : (
            <PeekAgenda
              buckets={agendaBuckets}
              today={today}
              onEventClick={handleEventClick}
            />
          )}
        </div>

        {/* Footer: open full calendar */}
        <div className="border-t border-border/40 px-3 py-2 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/bookings')}
            className="text-[11px] font-medium text-muted-foreground hover:text-foreground flex items-center gap-1"
          >
            Open Bookings Calendar
            <ExternalLink className="h-3 w-3" />
          </button>
          {!calendarConnected && (
            <button
              type="button"
              onClick={() => navigate('/integrations')}
              className="text-[10px] text-muted-foreground/70 hover:text-foreground"
              title="Connect a Nylas calendar to merge events"
            >
              + Nylas
            </button>
          )}
        </div>
      </aside>

      {/* Detail drawer — overlays the rail itself, not the dashboard.
          Keeps "stay on dashboard" intent intact. */}
      {detail && (
        <PeekEventDetail
          event={detail}
          onClose={() => setDetail(null)}
          onOpenInCalendar={() => {
            handleOpenInCalendar(detail)
            setDetail(null)
          }}
          railOpen={open}
        />
      )}
    </>
  )
}

// -----------------------------------------------------------------------------
// MiniMonth
// -----------------------------------------------------------------------------

interface MiniMonthProps {
  cursorMonth: Date
  selectedDay: Date
  today: Date
  eventDays: Set<string>
  onSelect: (day: Date) => void
}

function MiniMonth({ cursorMonth, selectedDay, today, eventDays, onSelect }: MiniMonthProps) {
  const monthStart = startOfMonth(cursorMonth)
  const monthEnd = endOfMonth(cursorMonth)
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 0 })
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 0 })

  const days: Date[] = []
  let d = gridStart
  while (d <= gridEnd) {
    days.push(d)
    d = addDays(d, 1)
  }

  return (
    <div>
      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((label, i) => (
          <div
            key={`${label}-${i}`}
            className="text-center text-[10px] font-medium text-muted-foreground/70 py-1"
          >
            {label}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {days.map(day => {
          const inMonth = isSameMonth(day, cursorMonth)
          const isSel = isSameDay(day, selectedDay)
          const isCurrent = isSameDay(day, today)
          const hasEvents = eventDays.has(format(day, 'yyyy-MM-dd'))
          return (
            <button
              key={day.toISOString()}
              type="button"
              onClick={() => onSelect(day)}
              className={cn(
                'relative h-8 rounded-md text-xs font-medium flex items-center justify-center transition-colors',
                !inMonth && 'text-muted-foreground/40',
                inMonth && !isSel && 'text-foreground hover:bg-accent',
                isSel && 'bg-brand-royal text-white shadow-sm',
                !isSel && isCurrent && 'ring-1 ring-brand-royal/60'
              )}
            >
              {format(day, 'd')}
              {hasEvents && !isSel && (
                <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full bg-brand-royal/70" />
              )}
              {hasEvents && isSel && (
                <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full bg-white/80" />
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// -----------------------------------------------------------------------------
// PeekAgenda
// -----------------------------------------------------------------------------

interface PeekAgendaProps {
  buckets: Array<{ day: Date; events: PeekEvent[] }>
  today: Date
  onEventClick: (ev: PeekEvent) => void
}

function PeekAgenda({ buckets, today, onEventClick }: PeekAgendaProps) {
  const anyEvents = buckets.some(b => b.events.length > 0)
  if (!anyEvents) {
    return (
      <div className="px-4 py-10 text-center">
        <CalendarIcon className="h-6 w-6 text-muted-foreground/40 mx-auto mb-2" />
        <p className="text-xs text-muted-foreground">No events in this window</p>
        <p className="text-[10px] text-muted-foreground/60 mt-1">Pick another day on the calendar above</p>
      </div>
    )
  }
  return (
    <div className="divide-y divide-border/40">
      {buckets.map(bucket => (
        <div key={bucket.day.toISOString()} className="px-3 py-2">
          <div className="flex items-baseline gap-2 mb-1.5 px-1">
            <span
              className={cn(
                'text-[11px] font-semibold',
                isSameDay(bucket.day, today) ? 'text-brand-royal' : 'text-foreground'
              )}
            >
              {dayLabel(bucket.day, today)}
            </span>
            <span className="text-[10px] text-muted-foreground">
              {format(bucket.day, 'MMM d')}
            </span>
          </div>
          {bucket.events.length === 0 ? (
            <p className="text-[11px] text-muted-foreground/60 px-1 py-1">No events scheduled</p>
          ) : (
            <ul className="space-y-1">
              {bucket.events.map(ev => (
                <li key={ev.id}>
                  <button
                    type="button"
                    onClick={() => onEventClick(ev)}
                    className="w-full text-left flex items-stretch gap-2 px-1 py-1.5 rounded-md hover:bg-accent/60 transition-colors"
                  >
                    <span
                      className={cn('w-1 rounded-full shrink-0', SOURCE_STYLES[ev.source].stripe)}
                      aria-hidden="true"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium text-foreground truncate leading-tight">
                        {ev.title}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        {formatEventTime(ev)}
                      </p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  )
}

// -----------------------------------------------------------------------------
// PeekEventDetail
// -----------------------------------------------------------------------------

interface PeekEventDetailProps {
  event: PeekEvent
  onClose: () => void
  onOpenInCalendar: () => void
  railOpen: boolean
}

function PeekEventDetail({ event, onClose, onOpenInCalendar, railOpen }: PeekEventDetailProps) {
  const navigate = useNavigate()
  const style = SOURCE_STYLES[event.source]
  const Icon = style.icon
  const b = event.booking
  const g = event.calendar
  const [transcriptOpen, setTranscriptOpen] = React.useState(false)

  const sentimentColor = (() => {
    const s = b?.sentiment?.toLowerCase()
    if (s === 'positive') return 'text-emerald-600 dark:text-emerald-400'
    if (s === 'negative') return 'text-rose-600 dark:text-rose-400'
    return 'text-muted-foreground'
  })()

  return (
    <div
      className="fixed top-0 z-[55] h-screen flex flex-col bg-card border-l border-border/50 shadow-2xl animate-in slide-in-from-right duration-200"
      style={{ right: railOpen ? `${PEEK_RAIL_WIDTH_PX}px` : '0px', width: '380px' }}
    >
      <div className="flex items-start justify-between px-4 py-3 border-b border-border/40">
        <div className="flex items-start gap-2 min-w-0">
          <span className={cn('w-1 self-stretch rounded-full shrink-0', style.stripe)} aria-hidden="true" />
          <div className="min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground flex items-center gap-1">
              <Icon className="h-3 w-3" /> {style.label}
            </p>
            <h4 className="text-sm font-heading font-semibold leading-tight mt-0.5 truncate">
              {event.title}
            </h4>
            {b && (
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                {b.addressed ? (
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
                    <CheckCircle2 className="h-3 w-3" /> Handled
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-600 dark:text-amber-400">
                    <AlertCircle className="h-3 w-3" /> Needs attention
                  </span>
                )}
                {b.qualificationScore != null && (
                  <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground">
                    <Star className="h-3 w-3" /> {b.qualificationScore}/10
                  </span>
                )}
                {b.sentiment && (
                  <span className={cn('text-[10px] font-medium capitalize', sentimentColor)}>
                    {b.sentiment}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="h-7 w-7 rounded-md hover:bg-accent flex items-center justify-center text-muted-foreground shrink-0"
          aria-label="Close detail"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 text-xs">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-0.5">When</p>
          <p className="text-foreground">{format(event.start, 'EEEE, MMM d, yyyy')}</p>
          <p className="text-muted-foreground mt-0.5">{formatEventTime(event)}</p>
        </div>

        {b?.phone && (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-0.5">Phone</p>
            <a href={`tel:${b.phone}`} className="text-brand-royal hover:underline">
              {formatPhoneDisplay(b.phone)}
            </a>
          </div>
        )}

        {b?.caseType && (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-0.5">Case type</p>
            <p>{b.caseType}</p>
          </div>
        )}

        {b?.summary && (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-0.5">Summary</p>
            <p className="text-foreground/90 whitespace-pre-wrap">{b.summary}</p>
          </div>
        )}

        {b?.note && (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-0.5 flex items-center gap-1">
              <StickyNote className="h-3 w-3" /> Note
            </p>
            <p className="text-foreground/90 whitespace-pre-wrap">{b.note}</p>
          </div>
        )}

        {/* Transcript — calls only. Collapsed by default; some transcripts run
            hundreds of lines and would dominate the drawer. */}
        {b?.transcript && b.type === 'call' && (
          <div>
            <button
              type="button"
              onClick={() => setTranscriptOpen(v => !v)}
              className="w-full flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground"
            >
              <span className="flex items-center gap-1">
                <FileText className="h-3 w-3" /> Transcript
              </span>
              <ChevronDown
                className={cn(
                  'h-3 w-3 transition-transform duration-150',
                  transcriptOpen && 'rotate-180'
                )}
              />
            </button>
            {transcriptOpen && (
              <pre className="mt-2 p-2 rounded-md bg-muted/50 text-[11px] leading-relaxed whitespace-pre-wrap font-sans text-foreground/85 max-h-64 overflow-y-auto">
                {b.transcript}
              </pre>
            )}
          </div>
        )}

        {/* Chat conversation link */}
        {b?.conversationId && b.type === 'chat' && (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-0.5">Conversation</p>
            <button
              type="button"
              onClick={() => navigate(`/conversations?id=${b.conversationId}`)}
              className="text-brand-royal hover:underline inline-flex items-center gap-1"
            >
              View chat history <ExternalLink className="h-3 w-3" />
            </button>
          </div>
        )}

        {/* Reminder status — bookings only */}
        {b && (b.attorneyReminderEnabled !== undefined || b.clientConfirmationEnabled !== undefined) && (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Reminders</p>
            <div className="space-y-1">
              {b.attorneyReminderEnabled !== undefined && (
                <p className="text-[11px]">
                  <span className="text-muted-foreground">Attorney: </span>
                  <span className={b.attorneyReminderEnabled ? 'text-foreground' : 'text-muted-foreground/70'}>
                    {b.attorneyReminderEnabled ? 'On' : 'Off'}
                  </span>
                  {b.reminderSent && (
                    <span className="ml-1 text-[10px] text-emerald-600 dark:text-emerald-400">· sent</span>
                  )}
                </p>
              )}
              {b.clientConfirmationEnabled !== undefined && (
                <p className="text-[11px]">
                  <span className="text-muted-foreground">Client confirmation: </span>
                  <span className={b.clientConfirmationEnabled ? 'text-foreground' : 'text-muted-foreground/70'}>
                    {b.clientConfirmationEnabled ? 'On' : 'Off'}
                  </span>
                  {b.confirmationSent && (
                    <span className="ml-1 text-[10px] text-emerald-600 dark:text-emerald-400">· sent</span>
                  )}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Connected calendar event fields */}
        {g?.location && (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-0.5">Location</p>
            <p>{g.location}</p>
          </div>
        )}
        {g?.description && (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-0.5">Description</p>
            <p className="text-foreground/90 whitespace-pre-wrap">{g.description}</p>
          </div>
        )}
      </div>

      <div className="border-t border-border/40 px-4 py-2.5">
        <button
          type="button"
          onClick={onOpenInCalendar}
          className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-brand-royal text-white text-xs font-medium hover:bg-brand-indigo transition-colors"
        >
          <ExternalLink className="h-3 w-3" />
          {event.calendar ? 'Open in Connected Calendar' : 'Open in Bookings Calendar'}
        </button>
      </div>
    </div>
  )
}
